import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import UserBasicInformation from "./userBasicInformation";
import UserContactInformation from "./userContactInformation";
import EditUserIcon from "@assets/icons/editUserIcon";
import AddUserIcon from "@assets/icons/addUserIcon";
import UserSettings from "./userSettings";
import UserDeactivationModal from "./Modal/userDeactivationModal";
import { useNavigate, useParams } from "react-router";
import { validateFormField } from "@common/helpers";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@store/store";
import { addUser, getUserById, updateUser } from "@store/addUserSlice";
import AddUserConfirmationModal from "./Modal/addUserConfirmationModal";

interface AddUserFormProps{
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>;
  showLoader: boolean
  setEditUserDetails: React.Dispatch<React.SetStateAction<boolean>>;
  editUserDetails: boolean
}

const AddUserForm: React.FC<AddUserFormProps> = ({setEditUserDetails, editUserDetails, setShowLoader, showLoader}) => {
  const [showUserDeactivationModal, setShowUserDeactivationModal] =
    useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState(false);
  const [updateUserInfo, setUpdateUserInfo] = useState(false);
  const [showAddUserConfirmationModal, setShowAddUserConfirmationModal] = useState(false);
  const {user} = useSelector((state: any) => state.user);
  const {userDetails, userDataLoading, addUserloading, addUserError, updateUserloading, updateUserError} = useSelector((state: any) => state.addUser);
  const { id } = useParams();
  const { securityGroups } = useSelector((state: any) => state.securityGroups);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [userData, setUserData] = React.useState({
    firstName: "",
    lastName: "",
    securityGroupId: "",
    emailID: "",
    countryCode: "",
    contactNumber: "",
    isActive: true,
    statusChangeReason: null,
    statusChangeComment:null
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    securityGroupId: "",
    emailID: "",
    countryCode: "",
    contactNumber: "",
  });
 
  useEffect(() => {
    if (id) {
      dispatch(getUserById({userId:id}));
      setEditUserDetails(true);
    }
  }, []);


  useEffect(()=>{
    if((!addUserloading && !addUserError)  || (!updateUserloading && !updateUserError)){
      setShowLoader(false)
      navigate("/users");
    }
  }, [addUserloading, updateUserloading]);

  useEffect(() => {
    if (editUserDetails && userDetails) {
      const group = securityGroups.find((group:any)=> group.id === userDetails.securityGroupId);
      const mapUserDetail = {
        firstName: userDetails?.firstName || "",
        lastName: userDetails?.lastName || "",
        securityGroupId: group ? userDetails?.securityGroupId :"",
        emailID: userDetails?.emailID || "",
        countryCode: userDetails?.countryCode || "",
        contactNumber: userDetails?.contactNumber || "",
        isActive: userDetails?.isActive,
        statusChangeReason: userDetails?.statusChangeReason || null,
        statusChangeComment: userDetails?.statusChangeComment || null,
      };
      setUserData((prev) => ({ ...prev, ...mapUserDetail }));
    }
  }, [editUserDetails, userDetails]);

  const hasChanges = React.useMemo(()=>{
      return ['firstName','lastName','securityGroupId', 'emailID', 'countryCode', 'contactNumber', 'isActive'].reduce((prev,curr)=> {
        return prev || userData[curr] !== userDetails[curr]
      },false)
  },[userDetails,userData])

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => {
    const { name, value } = event.target;
    validateFormField(name, value, setErrors);
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = () => {
    setShowUserDeactivationModal(true);
    setUserData((prev) => ({
      ...prev,
      statusChangeReason: null,
      statusChangeComment:null
    }));
  };

  const validateOnSubmit = () => {
    let tempErrors: any = { ...errors };
    let isValid = true;
    Object.entries(userData).forEach(([key, value]) => {
      if (!value && key !== "isActive" && key !== "statusChangeReason" && key !== "statusChangeComment") {
        tempErrors[key] = "Mandatory field";
        isValid = false;
      }
    });
    Object.entries(errors).forEach(([key, value]) => {
      if (value) {
        isValid = false;
      }
    });
    setErrors((prev) => ({ ...prev, ...tempErrors }));
    return isValid;
  };

  const handleSubmit = (action:string) => {
    let payload={
      hospitalId: user?.hospitalId,
      securityGroupId: userData?.securityGroupId,
      firstName: userData?.firstName.trim(),
      lastName: userData?.lastName.trim(),
      emailID: userData?.emailID.trim(),
      countryCode: userData?.countryCode,
      contactNumber: userData?.contactNumber.trim(),
      userTypeId: 2,
    }
    if (validateOnSubmit() && action==="Add User") {
       setShowAddUserConfirmationModal(true);
     }
     if (validateOnSubmit() && action==="submit") {
      dispatch(addUser(payload));
      setShowLoader(true);
       }
    if (validateOnSubmit() && action==="Update User") {
      payload={
        ...payload,
        userId: userDetails?.userId,
        isActive: userData?.isActive,
        zendeskSupportGroupId: userDetails?.zendeskSupportGroupId,
        zendeskSupportGroup: userDetails?.zendeskSupportGroup,
        zendeskUserId: userDetails?.zendeskUserId,
        zendeskOrgId: userDetails?.zendeskOrgId,
        isOnCallStatus:false,
        statusChangeReason: userData?.statusChangeReason,
        statusChangeComment: userData?.statusChangeComment,
      } as any
    dispatch(updateUser(payload));
    setShowLoader(true);
    }
  };

  const handleUserDeactivation = (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => {
    const { name, value } = event.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUserStatus = () => {
    setUserData((prev) => ({
      ...prev,
      isActive: !prev.isActive,
    }));
    setConfirmationMessage(false);
    setShowUserDeactivationModal(false);
  };

  return (
    <>
    {userDataLoading ?
      <Box sx={{
          height: "calc(100vh - 191px)",
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          backgroundColor:"#fff"
        }} >
           <CircularProgress size="20px"/>
        </Box> :
    <Box
      className="md:shadow-md"
      sx={{
        height: "calc(100vh - 191px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "auto",
        backgroundColor: { xs: "#F1F5F7", sm: "#fff" },
      }}
    >
      <Box>
        <Box component="div" className="pt-2 px-2 bg-[#fff] hidden md:block">
          <Box className="flex flex-row justify-between items-center border-dotted border-b-2 md:border-[#0000004D] border-[#666666]">
            <Box className="flex flex-row items-center">
              <Typography className="md:px-2 py-3 px-1">
                <AddUserIcon />
              </Typography>
              <Typography className="pt-1 flex items-center text-[#090909]" sx={{fontWeight:600}}>
                {editUserDetails ? "Edit User" : "Add User"}
              </Typography>
            </Box>
            {editUserDetails && (
              <Typography onClick={()=> setUpdateUserInfo(true)} className="cursor-pointer">
                <EditUserIcon />
              </Typography>
            )}
          </Box>
        </Box>
        <UserBasicInformation
          errors={errors}
          handleChange={handleChange}
          userData={userData}
          updateUserInfo={updateUserInfo}
          editUserDetails={editUserDetails}
          setUpdateUserInfo={setUpdateUserInfo}
        />
        <UserContactInformation
          errors={errors}
          userData={userData}
          handleChange={handleChange}
          updateUserInfo={updateUserInfo}
        />
        {editUserDetails && (
          <UserSettings
            handleSwitchChange={handleSwitchChange}
            active={userData.isActive}
          />
        )}
      </Box>

      <Box className="w-full mt-4 bg-white mt-4 md:flex flex-row-reverse justify-between md:border-dotted md:border-t-2 md:border-[#0000004D] md:border-[#666666]">
        <Box className="p-4 flex gap-4 justify-between md:w-[50%] lg:w-[100%]">
          <Button
            sx={{
              backgroundColor: "#fff",
              color: "#318FE0",
              textTransform: "none",
              padding: "10px 18px",
              fontSize: "1rem",
              width: {xs:"50%", md:"14%"},
              border: "1px solid #318FE0",
            }}
              onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
              disabled={!hasChanges}
            sx={{
              backgroundColor: hasChanges?"#318FE0":"#A1A3A6",
              color: "#fff !important",
              textTransform: "none",
              padding: "10px 18px",
              fontSize: "1rem",
              width: {xs:"50%", md:"14%"},
            }}
            onClick={()=>handleSubmit(editUserDetails ? "Update User" : "Add User")}
          ><Typography className="!flex !gap-2 !items-center">
            {(showLoader && updateUserloading) &&  <CircularProgress style={{'color': 'white'}} size="20px"/>}
            {editUserDetails ? "Update User" : "Add User"}</Typography>
          </Button>
        </Box>
      </Box>
      <UserDeactivationModal
        showUserDeactivationModal={showUserDeactivationModal}
        setShowUserDeactivationModal={setShowUserDeactivationModal}
        handleUserDeactivation={handleUserDeactivation}
        handleUserStatus={handleUserStatus}
        confirmationMessage={confirmationMessage}
        setConfirmationMessage={setConfirmationMessage}
        userData={userData}
        setUserData={setUserData}
      />
      <AddUserConfirmationModal 
        showAddUserConfirmationModal={showAddUserConfirmationModal} 
        setShowAddUserConfirmationModal={setShowAddUserConfirmationModal} 
        handleSubmit={handleSubmit} 
        showLoader={showLoader}
        userData={userData}/>
    </Box>}
    </>
  );
};

export default AddUserForm;
